import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import {
  EventDto,
  EventService,
  ExhibitorDto,
  ExhibitorService,
} from '@millionshow/api-client';
import { Icon } from '@millionshow/client-libs';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import PaginationComponent from '../../components/pagination/pagination.component';
import { NotFoundComponent } from '../../components/not-found/not-found.component';
import { InfiniteScrollComponent } from '../../components/infinite-scroll/infinite-scroll.component';
import { formatDateRange } from '../../utils/date-formatter';
import { ScreenSizeStore } from '../../stores/screen-size.store';

export type ExhibitorsTab = 'brands' | 'past-events';

@Component({
  selector: 'app-exhibitors',
  imports: [
    CommonModule,
    RouterLink,
    Icon,
    PaginationComponent,
    NotFoundComponent,
    InfiniteScrollComponent,
  ],
  templateUrl: './exhibitors.page.html',
  styleUrl: './exhibitors.page.css',
})
export default class ExhibitorsPage {
  private readonly exhibitorService =
    inject<ExhibitorService>(ExhibitorService);
  private readonly eventService = inject<EventService>(EventService);
  readonly screenSizeStore = inject(ScreenSizeStore);
  isMobile = this.screenSizeStore.isMobileSize;

  formatDateRange = formatDateRange;

  selectedTab = signal<ExhibitorsTab>('brands');

  // ────────────────────────────────────────────
  // 참가브랜드 탭
  // ────────────────────────────────────────────
  query = signal<string>('');
  pageNo = signal<number>(1);

  // PC 페이지 사이즈
  readonly PC_PAGE_SIZE = 8;

  // 모바일: 초기/추가 단위
  readonly MO_BRANDS_INIT = 4;
  readonly MO_BRANDS_STEP = 4;
  moBrandsDisplayCount = signal<number>(this.MO_BRANDS_INIT);

  private searchSubject = new Subject<string>();

  constructor() {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(q => {
        this.query.set(q);
        this.pageNo.set(1);
        this.moBrandsDisplayCount.set(this.MO_BRANDS_INIT);
      });
  }

  changeTab(tab: ExhibitorsTab) {
    this.selectedTab.set(tab);
    this.query.set('');
    this.pageNo.set(1);
    this.moBrandsDisplayCount.set(this.MO_BRANDS_INIT);
    this.pastPageNo.set(1);
    this.moPastDisplayCount.set(this.MO_PAST_INIT);
  }

  // ── PC: brands 페이지네이션 (api 호출)──
  $data = rxResource({
    stream: ({ params }) =>
      this.exhibitorService.exhibitorControllerSearchV1({
        ...params,
        isExposed: true,
      }),
    params: () => ({
      pageNo: this.pageNo(),
      pageSize: this.PC_PAGE_SIZE,
      query: this.query(),
      type: 'STARTFAIR' as ExhibitorDto['type'],
    }),
  });

  list = computed(() => (this.$data.value()?.items ?? []) as ExhibitorDto[]);
  totalCount = computed(() => this.$data.value()?.pageInfo?.totalItems || 0);
  currentPage = computed(() => this.pageNo());
  totalPages = computed<number[]>(() => {
    const count = Math.max(1, Math.ceil(this.totalCount() / this.PC_PAGE_SIZE));
    return Array.from({ length: count }, (_, i) => i + 1);
  });

  // ── 모바일: brands 전체 fetch → 클라이언트 slice ──
  // products.page.ts와 동일한 방식: 한 번에 전체 가져오고 displayCount로 slice
  $moBrandsAll = rxResource({
    stream: ({ params }) =>
      this.exhibitorService.exhibitorControllerSearchV1({
        ...params,
        isExposed: true,
      }),
    params: () => ({
      pageNo: 1,
      pageSize: 1000, // 전체
      query: this.query(),
      type: 'STARTFAIR' as ExhibitorDto['type'],
    }),
  });

  moBrandsAll = computed(
    () => (this.$moBrandsAll.value()?.items ?? []) as ExhibitorDto[]
  );
  moBrandsList = computed(() =>
    this.moBrandsAll().slice(0, this.moBrandsDisplayCount())
  );
  moBrandsHasMore = computed(
    () => this.moBrandsDisplayCount() < this.moBrandsAll().length
  );

  loadMoreBrands() {
    if (!this.moBrandsHasMore()) return;
    this.moBrandsDisplayCount.update(c => c + this.MO_BRANDS_STEP);
  }

  // ────────────────────────────────────────────
  // 지난박람회 탭
  // ────────────────────────────────────────────
  pastPageNo = signal<number>(1);
  readonly PC_PAST_PAGE_SIZE = 8;

  // 모바일: 초기/추가 단위
  readonly MO_PAST_INIT = 4;
  readonly MO_PAST_STEP = 4;
  moPastDisplayCount = signal<number>(this.MO_PAST_INIT);

  // ── PC: past 페이지네이션 ──
  $pastEvents = rxResource({
    stream: ({ params }) =>
      this.eventService.eventControllerSearchV1({ ...params }),
    params: () => ({
      pageNo: this.pastPageNo(),
      pageSize: this.PC_PAST_PAGE_SIZE,
      type: 'STARTFAIR' as EventDto['type'],
      orderBy: 'startAt',
      align: 'desc' as const,
    }),
  });

  pastEvents = computed(() =>
    ((this.$pastEvents.value()?.items ?? []) as EventDto[]).filter(
      e => e.status === 'FINISHED' || e.status === 'CANCELLED'
    )
  );
  pastTotalCount = computed(
    () => this.$pastEvents.value()?.pageInfo?.totalItems || 0
  );
  pastCurrentPage = computed(() => this.pastPageNo());
  pastTotalPages = computed<number[]>(() => {
    const count = Math.max(
      1,
      Math.ceil(this.pastTotalCount() / this.PC_PAST_PAGE_SIZE)
    );
    return Array.from({ length: count }, (_, i) => i + 1);
  });

  // ── 모바일: past 전체 fetch → 클라이언트 slice ──
  $moPastAll = rxResource({
    stream: ({ params }) =>
      this.eventService.eventControllerSearchV1({ ...params }),
    params: () => ({
      pageNo: 1,
      pageSize: 1000, // 전체
      type: 'STARTFAIR' as EventDto['type'],
      orderBy: 'startAt',
      align: 'desc' as const,
    }),
  });

  moPastAll = computed(() =>
    ((this.$moPastAll.value()?.items ?? []) as EventDto[]).filter(
      e => e.status === 'FINISHED' || e.status === 'CANCELLED'
    )
  );
  moPastList = computed(() =>
    this.moPastAll().slice(0, this.moPastDisplayCount())
  );
  moPastHasMore = computed(
    () => this.moPastDisplayCount() < this.moPastAll().length
  );

  loadMorePast() {
    if (!this.moPastHasMore()) return;
    this.moPastDisplayCount.update(c => c + this.MO_PAST_STEP);
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchSubject.next(input.value);
  }

  onPageChange(page: number): void {
    this.pageNo.set(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onPastPageChange(page: number): void {
    this.pastPageNo.set(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
